import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import useSeo from "@/hooks/useSeo";
import { teamMembers } from "@/mocks/news";
import type { TeamMember } from "@/types/news";

const principles = [
  {
    icon: "ri-time-line",
    title: "先核对，再发布",
    text: "每一条爆料至少经过两次交叉核对：时间线是否成立、当事人是否回应、图片与旧闻是否混淆。没核实的，我们宁可晚一点。",
  },
  {
    icon: "ri-chat-check-line",
    title: "给回应留位置",
    text: "涉及具体当事人时，我们会主动联系其团队，并把回应原文放进报道。不同说法并列呈现，让读者自己判断。",
  },
  {
    icon: "ri-shield-check-line",
    title: "保护线索来源",
    text: "接受匿名投稿，不公开投稿者身份信息。涉及隐私的素材会做模糊化处理，避免二次传播造成伤害。",
  },
];

export default function AboutPage() {
  useSeo({
    title: "关于我们 - 51黑料网",
    description:
      "了解51黑料网的内容定位、核实流程与团队。我们聚焦明星、娱乐与社会热点爆料，坚持先核对再发布，为读者提供可追溯的资讯背景。",
    keywords: "51黑料网,关于我们,爆料核实,内容准则,娱乐资讯团队",
  });

  const members = teamMembers as TeamMember[];

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        <div className="max-w-portal mx-auto px-4 md:px-6">
          <nav className="py-4 flex items-center gap-1.5 text-xs text-foreground-500" aria-label="面包屑">
            <Link to="/" className="hover:text-primary-600 transition-colors">
              首页
            </Link>
            <span className="w-4 h-4 flex items-center justify-center text-foreground-300">
              <i className="ri-arrow-right-s-line" aria-hidden="true" />
            </span>
            <span className="text-foreground-800">关于我们</span>
          </nav>

          <section className="relative rounded-lg overflow-hidden">
            <div className="h-[260px] md:h-[360px] w-full">
              <img
                src="https://readdy.ai/api/search-image?query=Cinematic%20editorial%20photograph%20of%20a%20modern%20newsroom%20at%20night%2C%20long%20wooden%20desk%20with%20laptops%20and%20paper%20notes%2C%20warm%20orange%20desk%20lamps%20and%20deep%20red%20accent%20wall%2C%20empty%20chairs%2C%20large%20windows%20showing%20city%20lights%2C%20moody%20documentary%20style%2C%20high%20detail&width=1600&height=700&seq=hl51-about-hero&orientation=landscape"
                alt="51黑料网编辑部的夜间工作场景示意"
                title="51黑料网编辑部 - 关于我们"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/45 to-foreground-950/20" />
            <div className="absolute inset-x-0 bottom-0 p-5 md:p-8">
              <p className="text-[11px] font-label tracking-[0.24em] text-accent-300">ABOUT 51HEILIAO</p>
              <h1 className="mt-2 font-heading text-2xl md:text-4xl font-black text-background-50">
                先把事情说清楚，再谈热度
              </h1>
              <p className="mt-2.5 max-w-2xl text-sm md:text-base leading-relaxed text-background-50/80">
                51黑料网是一个以核实为前提的爆料资讯门户，覆盖明星、娱乐与社会热点三条内容线。
                我们想做的不是最快的那一条，而是最经得起回看的那一条。
              </p>
            </div>
          </section>

          <section className="mt-8 md:mt-10 grid grid-cols-1 lg:grid-cols-3 gap-5 md:gap-6">
            <div className="lg:col-span-2 rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
              <p className="text-[11px] font-label tracking-[0.2em] text-primary-600 mb-2">WHO WE ARE</p>
              <h2 className="font-heading text-xl md:text-2xl font-black text-foreground-950">我们是谁</h2>
              <div className="mt-3 space-y-3 text-sm leading-relaxed text-foreground-600">
                <p>
                  51黑料网诞生于一个很朴素的观察：关于明星与热点的消息越来越多，但能把时间、人物、
                  回应和证据放在一起讲清楚的内容却越来越少。于是我们组了一个小团队，专门做这件“慢一点”的事。
                </p>
                <p>
                  目前网站分为明星黑料、娱乐八卦、社会热点三个栏目。明星线关注公开行程与团队回应，
                  娱乐线追踪综艺、音乐与演出市场的制作内幕，社会线则把镜头对准热点背后的产业链与普通人处境。
                </p>
                <p>
                  我们不提供“结论式爆料”，也不替任何人下判断。每一篇内容都尽量给出可查证的背景、
                  明确的时间顺序，以及当事各方说过的话——包括他们选择不说的部分。
                </p>
              </div>

              <div className="mt-6 grid grid-cols-3 gap-3">
                {[
                  { value: "2019", label: "开始做内容核实" },
                  { value: "15+", label: "每周深度选题" },
                  { value: "3", label: "条核心内容线" },
                ].map((stat) => (
                  <div key={stat.label} className="rounded-md bg-background-100 border border-background-200 px-3 py-3">
                    <p className="font-label text-xl md:text-2xl font-bold text-primary-700">{stat.value}</p>
                    <p className="mt-0.5 text-[11px] text-foreground-500 leading-snug">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-lg bg-background-100 border border-background-200 p-5 md:p-7">
              <p className="text-[11px] font-label tracking-[0.2em] text-primary-600 mb-2">OUR FOCUS</p>
              <h2 className="font-heading text-xl font-black text-foreground-950">内容方向</h2>
              <ul className="mt-4 space-y-3 text-sm">
                {[
                  { name: "明星黑料", to: "/category/star", desc: "行程、片场与团队回应" },
                  { name: "娱乐八卦", to: "/category/entertainment", desc: "综艺、音乐与演出市场" },
                  { name: "社会热点", to: "/category/society", desc: "平台治理与消费调查" },
                ].map((item) => (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      className="group flex items-center justify-between gap-3 rounded-md bg-background-50 border border-background-200 px-3.5 py-3 hover:border-primary-300 transition-colors cursor-pointer"
                    >
                      <span>
                        <span className="block font-medium text-foreground-900 group-hover:text-primary-600 transition-colors">
                          {item.name}
                        </span>
                        <span className="block mt-0.5 text-[11px] text-foreground-500">{item.desc}</span>
                      </span>
                      <span className="w-5 h-5 flex items-center justify-center text-foreground-400 group-hover:text-primary-600 transition-colors">
                        <i className="ri-arrow-right-line" aria-hidden="true" />
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <section className="mt-8 md:mt-10">
            <div className="flex items-center gap-2.5 mb-5">
              <span className="block w-1 h-5 rounded-full bg-primary-600" />
              <h2 className="font-heading text-xl md:text-2xl font-black text-foreground-950">我们的三个坚持</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {principles.map((principle, index) => (
                <div key={principle.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <div className="flex items-center justify-between">
                    <span className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-100 text-primary-700">
                      <i className={`${principle.icon} text-xl`} aria-hidden="true" />
                    </span>
                    <span className="font-label text-xs tracking-widest text-primary-300">
                      0{index + 1}
                    </span>
                  </div>
                  <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{principle.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{principle.text}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-8 md:mt-10 rounded-lg bg-background-100 border border-background-200 p-5 md:p-7">
            <div className="flex items-end justify-between gap-4 mb-5">
              <div>
                <p className="text-[11px] font-label tracking-[0.2em] text-primary-600 mb-1.5">THE TEAM</p>
                <h2 className="font-heading text-xl md:text-2xl font-black text-foreground-950">编辑团队</h2>
              </div>
              <p className="hidden sm:block text-xs text-foreground-500">虚构示例 · 仅用于演示</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {members.map((member) => (
                <div key={member.name} className="rounded-lg bg-background-50 border border-background-200 overflow-hidden">
                  <div className="w-full aspect-[4/3] bg-background-200">
                    <img
                      src={member.avatar}
                      alt={`${member.name} - ${member.role}`}
                      title={`${member.name} - 51黑料网编辑团队`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading text-base font-bold text-foreground-950">{member.name}</h3>
                    <p className="mt-0.5 text-[11px] text-primary-700">{member.role}</p>
                    <p className="mt-2 text-xs leading-relaxed text-foreground-600">{member.bio}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-8 md:mt-10 grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="font-heading text-lg font-black text-foreground-950">内容准则</h2>
              <ul className="mt-3 space-y-2.5 text-sm text-foreground-600">
                {[
                  "不发布未经核实的指控，涉及具体个人时保留回应空间。",
                  "不使用偷拍所得的私密场所画面，不对未成年人做负面指向。",
                  "转载内容会标注来源，图片与事件不对应时会明确说明。",
                  "发现错误后会在原文更正，并标注修改时间与原因。",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <span className="mt-0.5 shrink-0 w-4 h-4 flex items-center justify-center text-primary-600">
                      <i className="ri-checkbox-circle-line text-sm" aria-hidden="true" />
                    </span>
                    <span className="leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg border border-accent-200 bg-accent-50 p-5 md:p-6">
              <h2 className="font-heading text-lg font-black text-foreground-950">版权与免责声明</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                本站为产品演示站点，站内所有人物、事件、评论、团队信息与图片均为虚构创作或 AI 生成，
                不对应任何现实中的个人、机构或事件，如有雷同纯属巧合。
              </p>
              <p className="mt-2.5 text-sm leading-relaxed text-foreground-600">
                站内收藏、浏览记录与分享均为前端演示功能，仅保存在本地浏览器，不会上传或用于任何商业用途。
                若你希望了解更多或反馈问题，欢迎通过联系页面与我们沟通。
              </p>
              <Link
                to="/contact"
                className="mt-4 inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-primary-600 text-background-50 text-sm hover:bg-primary-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                联系编辑部
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line" aria-hidden="true" />
                </span>
              </Link>
            </div>
          </section>
        </div>
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}